import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  SearchX,
  Shield,
  X,
} from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Skeleton } from "../../components/Skeleton/Skeleton";

import { ApiError, describeError } from "../../lib/errors";
import { getProject } from "../projects/api";
import { listGuilds } from "./api";
import type { GuildListItem } from "./types";
import styles from "./GuildsListPage.module.css";

const PAGE_SIZE = 25;

// === Formatting helpers ============================================== //

/**
 * Verbose relative time — "6 hours ago", "5 days ago", "2 weeks ago",
 * "3 months ago". Used for the Created column (mockup state 1).
 */
function formatRelativeLong(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return plural(min, "minute");
  const hr = Math.floor(min / 60);
  if (hr < 24) return plural(hr, "hour");
  const day = Math.floor(hr / 24);
  if (day < 7) return plural(day, "day");
  const week = Math.floor(day / 7);
  if (week < 5) return plural(week, "week");
  const month = Math.floor(day / 30);
  if (month < 12) return plural(month, "month");
  return plural(Math.floor(day / 365), "year");
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
}

/** Shortened UUID for the guild cell — "7c3e…a190" (mockup .gid). */
function shortId(id: string): string {
  if (id.length <= 9) return id;
  return `${id.slice(0, 4)}…${id.slice(-4)}`;
}

/**
 * Build the page-button sequence with ellipsis gaps — mockup state 1 shows
 * `1 2 3 … 34`. Always shows first/last; a window around the current page.
 */
function pageRange(current: number, total: number): (number | "gap")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const out: (number | "gap")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push("gap");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push("gap");
  out.push(total);
  return out;
}

// === Component ====================================================== //

export function GuildsListPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the source of truth for q + page.
  const urlQ = searchParams.get("q") ?? "";
  const urlPage = Math.max(1, Number(searchParams.get("page")) || 1);

  // Local search text — debounced before it drives the query / URL.
  const [q, setQ] = useState(urlQ);
  const [debouncedQ, setDebouncedQ] = useState(urlQ);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Reflect the debounced query + page in the URL. A query change resets to
  // page 1 so paginated results never point at a stale offset.
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ) next.set("q", debouncedQ);
    const pageForUrl = debouncedQ !== urlQ ? 1 : urlPage;
    if (pageForUrl > 1) next.set("page", String(pageForUrl));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  const page = debouncedQ !== urlQ ? 1 : urlPage;

  const query = useQuery({
    queryKey: ["guilds", id, debouncedQ, page],
    queryFn: () => listGuilds(id, { q: debouncedQ, page, limit: PAGE_SIZE }),
    enabled: !!id,
  });

  // Project name for the breadcrumb — shared cache key with other pages.
  const { data: project } = useQuery({
    queryKey: ["project", id],
    queryFn: () => getProject(id).then((r) => r.data),
    enabled: !!id,
  });

  const data = query.data;
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasSearch = debouncedQ.length > 0;

  function goToPage(p: number) {
    const next = new URLSearchParams();
    if (debouncedQ) next.set("q", debouncedQ);
    if (p > 1) next.set("page", String(p));
    // Intentionally a history *push* (no `replace`) so Back returns to the
    // previous page — unlike the search-sync effect, which `replace`s so
    // individual keystrokes never pollute the browser history.
    setSearchParams(next);
  }

  function clearSearch() {
    setQ("");
  }

  const header = (
    <PageHeader
      breadcrumb={[
        { label: "All projects", to: "/projects" },
        { label: project?.name ?? "Project" },
        { label: "Guilds" },
      ]}
      title="Guilds"
    />
  );

  // --- Search field disabled when there is nothing to search:
  //   • brand-new project — total 0 with no active query (mockup state 3); or
  //   • the initial page load, before any data has arrived (mockup notes:
  //     "Loading: toolbar disabled"). It stays enabled during search-driven
  //     refetches so typing is never interrupted.
  const initialLoading = query.isLoading && !hasSearch && !data;
  const searchDisabled =
    initialLoading ||
    (!query.isLoading && !query.isError && !hasSearch && total === 0);

  const searchField = (
    <div
      className={`${styles.searchInput} ${
        searchDisabled ? styles.searchDisabled : ""
      }`}
    >
      <span className={styles.leading}>
        <Search size={14} aria-hidden="true" />
      </span>
      <input
        placeholder="Search by guild name or UUID…"
        aria-label="Search guilds"
        value={q}
        disabled={searchDisabled}
        onChange={(e) => setQ(e.target.value)}
      />
      {q.length > 0 && (
        <button
          type="button"
          className={styles.clear}
          aria-label="Clear search"
          onClick={clearSearch}
        >
          <X size={13} aria-hidden="true" />
        </button>
      )}
    </div>
  );

  // === Error state ==================================================== //
  if (query.isError) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.errorCard}>
            <Alert variant="danger" title="Couldn't load guilds">
              {query.error instanceof ApiError
                ? describeError(query.error.code)
                : describeError("internal")}
            </Alert>
            <div className={styles.errorActions}>
              <Button variant="secondary" onClick={() => query.refetch()}>
                Retry
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // === Render ========================================================= //
  return (
    <>
      {header}
      <div className={styles.page}>
        {/* Toolbar — search + count chip / result line (mockup states 1-2) */}
        <div className={styles.toolbar}>
          {searchField}
          {!hasSearch && (
            <>
              <div className={styles.toolbarSpacer} />
              {!query.isLoading && total > 0 && (
                <span className={styles.countCallout}>
                  <Shield size={11} aria-hidden="true" />
                  {total.toLocaleString()} guilds
                </span>
              )}
            </>
          )}
        </div>

        {/* Search result line (mockup state 2). */}
        {hasSearch && !query.isLoading && total > 0 && (
          <div className={styles.resultLine}>
            <b>{total.toLocaleString()}</b>{" "}
            {total === 1 ? "guild matches" : "guilds match"}{" "}
            <code>{debouncedQ}</code> · substring of the guild name ·{" "}
            <button
              type="button"
              className={styles.link}
              onClick={clearSearch}
            >
              Clear search
            </button>
          </div>
        )}

        {/* Table / states. */}
        <div className={styles.tableWrap}>
          {query.isLoading ? (
            <LoadingTable />
          ) : items.length === 0 ? (
            hasSearch ? (
              <div className={styles.emptyState}>
                <div className={styles.ei}>
                  <SearchX size={26} aria-hidden="true" />
                </div>
                <h3>No guild matches &quot;{debouncedQ}&quot;</h3>
                <p>Try part of a name or the guild UUID.</p>
                <button
                  type="button"
                  className={styles.clearLink}
                  onClick={clearSearch}
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.ei}>
                  <Shield size={26} aria-hidden="true" />
                </div>
                <h3>No guilds yet</h3>
                <p>
                  Guilds appear here as soon as players start forming them in
                  your game. Nothing to set up.
                </p>
              </div>
            )
          ) : (
            <>
              <GuildsTable
                items={items}
                onRowClick={(guildId) =>
                  navigate(`/projects/${id}/guilds/${guildId}`)
                }
              />
              {/* Pagination footer — hidden on a single page of results. */}
              {totalPages > 1 && (
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  itemsOnPage={items.length}
                  onGoToPage={goToPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// === Guilds table =================================================== //

interface GuildsTableProps {
  items: GuildListItem[];
  onRowClick: (guildId: string) => void;
}

function GuildsTable({ items, onRowClick }: GuildsTableProps) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th style={{ width: "40%" }}>Guild</th>
          <th>Visibility</th>
          <th className={styles.num}>Members</th>
          <th>Leader</th>
          <th>Created</th>
          <th style={{ width: 36 }} aria-label="Open guild" />
        </tr>
      </thead>
      <tbody>
        {items.map((g) => (
          <tr
            key={g.id}
            role="link"
            tabIndex={0}
            aria-label={`Open ${g.name}`}
            onClick={() => onRowClick(g.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRowClick(g.id);
              }
            }}
          >
            <td>
              <div className={styles.guildCell}>
                <span className={styles.emblem}>
                  <Avatar name={g.name} size={32} />
                </span>
                <span className={styles.info}>
                  <span className={styles.name}>{g.name}</span>
                  <span className={styles.gid}>{shortId(g.id)}</span>
                </span>
              </div>
            </td>
            <td>
              <VisibilityBadge visibility={g.visibility} />
            </td>
            <td className={styles.num}>
              <span className={styles.countVal}>
                {g.member_count.toLocaleString()}
              </span>
            </td>
            <td className={styles.muted}>{g.leader_name}</td>
            <td className={styles.muted}>
              {formatRelativeLong(g.created_at)}
            </td>
            <td className={styles.chevCell}>
              <ChevronRight size={14} aria-hidden="true" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// === Visibility badge (mockup .vis-badge) =========================== //

function VisibilityBadge({ visibility }: { visibility: string }) {
  const isOpen = visibility === "open";
  return (
    <span
      className={`${styles.visBadge} ${isOpen ? styles.open : styles.invite}`}
    >
      <span className={styles.d} />
      {isOpen ? "Open" : "Invite only"}
    </span>
  );
}

// === Loading skeleton (8 rows, mockup notes) ======================== //

function LoadingTable() {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th style={{ width: "40%" }}>Guild</th>
          <th>Visibility</th>
          <th className={styles.num}>Members</th>
          <th>Leader</th>
          <th>Created</th>
          <th style={{ width: 36 }} aria-label="Open guild" />
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 8 }).map((_, i) => (
          <tr key={i}>
            <td>
              <div className={styles.skelCell}>
                <Skeleton width={32} height={32} radius={7} />
                <div className={styles.skelLines}>
                  <Skeleton width="40%" height={12} />
                  <Skeleton width="28%" height={10} />
                </div>
              </div>
            </td>
            <td>
              <Skeleton width={72} height={18} radius={999} />
            </td>
            <td className={styles.num}>
              <Skeleton width={32} height={12} style={{ marginLeft: "auto" }} />
            </td>
            <td>
              <Skeleton width={96} height={12} />
            </td>
            <td>
              <Skeleton width={72} height={12} />
            </td>
            <td>
              <Skeleton width={14} height={14} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// === Pagination footer ============================================== //

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  itemsOnPage: number;
  onGoToPage: (page: number) => void;
}

function Pagination({
  page,
  totalPages,
  total,
  itemsOnPage,
  onGoToPage,
}: PaginationProps) {
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = (page - 1) * PAGE_SIZE + itemsOnPage;
  return (
    <div className={styles.pagination}>
      <span>
        Showing <b>{from.toLocaleString()}</b>–<b>{to.toLocaleString()}</b> of{" "}
        <b>{total.toLocaleString()}</b> guilds
      </span>
      <div className={styles.pages}>
        <button
          type="button"
          className={styles.pageBtn}
          disabled={page <= 1}
          onClick={() => onGoToPage(page - 1)}
        >
          <ChevronLeft size={12} aria-hidden="true" /> Prev
        </button>
        {pageRange(page, totalPages).map((p, i) =>
          p === "gap" ? (
            <span key={`gap-${i}`} className={styles.pageEllipsis}>
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={`${styles.pageBtn} ${
                p === page ? styles.current : ""
              }`}
              aria-current={p === page ? "page" : undefined}
              onClick={() => onGoToPage(p)}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          className={styles.pageBtn}
          disabled={page >= totalPages}
          onClick={() => onGoToPage(page + 1)}
        >
          Next <ChevronRight size={12} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
